/**
 * FlowDiagram Component
 * Visual representation of agent execution flow
 */

import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { AgentStep } from '@/types/research.types';

interface FlowDiagramProps {
  steps: AgentStep[];
  onStepPress?: (type: AgentStep['type']) => void;
}

export const FlowDiagram: React.FC<FlowDiagramProps> = ({ steps, onStepPress }) => {
  const getStepIcon = (type: string) => {
    const icons: Record<string, any> = {
      planning: 'bulb',
      searching: 'search',
      extracting: 'funnel',
      drafting: 'create',
      critiquing: 'eye',
      refining: 'sparkles',
    };
    return icons[type] || 'ellipse';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete':
        return Colors.status.success;
      case 'error':
        return Colors.status.error;
      default:
        return Colors.text.tertiary;
    }
  };

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.container}>
      <View style={styles.flow}>
        {steps.map((step, index) => (
          <React.Fragment key={step.type}>
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => onStepPress?.(step.type)}
              style={styles.stepNode}
            >
              <View
                style={[
                  styles.iconContainer,
                  { backgroundColor: `${getStepColor(step.status)}20` },
                ]}
              >
                <Ionicons
                  name={getStepIcon(step.type)}
                  size={24}
                  color={getStepColor(step.status)}
                />
              </View>
              <Text style={styles.stepLabel}>{step.type}</Text>
              {step.cost !== undefined && (
                <Text style={styles.stepCost}>${step.cost.toFixed(3)}</Text>
              )}
              {step.endTime && step.startTime && (
                <Text style={styles.stepDuration}>
                  {((step.endTime - step.startTime) / 1000).toFixed(1)}s
                </Text>
              )}
              {step.status === 'in_progress' && (step as any)?.data?.progress && (
                <Text style={styles.stepProgress}>{(step as any).data.progress}</Text>
              )}
            </TouchableOpacity>

            {index < steps.length - 1 && (
              <View style={styles.arrow}>
                <Ionicons name="arrow-forward" size={20} color={Colors.text.tertiary} />
              </View>
            )}
          </React.Fragment>
        ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  flow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepNode: {
    alignItems: 'center',
    minWidth: 100,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  stepLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.primary,
    textTransform: 'capitalize',
    marginBottom: 2,
  },
  stepCost: {
    fontSize: 11,
    color: Colors.text.tertiary,
  },
  stepDuration: {
    fontSize: 11,
    color: Colors.accent.primary,
    marginTop: 2,
  },
  arrow: {
    marginHorizontal: 12,
  },
});
